import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type {
  ButtonHTMLAttributes,
  CSSProperties,
  InputHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
  ReactNode,
} from 'react'
import { STAGE_META, type OpportunityStage } from '@reptree/shared'
import { trackModalOpen } from '../lib/modal-open'

export function Button({
  variant = 'primary',
  arrow = false,
  className = '',
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'ghost' | 'subtle'; arrow?: boolean }) {
  const base =
    variant === 'primary'
      ? 'btn-brass'
      : variant === 'ghost'
        ? 'btn-ghost'
        : 'inline-flex items-center gap-1.5 rounded-nav px-3 py-1.5 hover:bg-[color:rgb(var(--tint-1))]'
  return (
    <button className={`${base} disabled:opacity-50 ${className}`} {...props}>
      {children}
      {arrow && variant === 'primary' && <span className="cta-arrow">→</span>}
    </button>
  )
}

/** The house close affordance: an X icon button. Modals and inline panels pin it top-right so it
 *  is always findable, especially on mobile where a trailing text "Close" gets lost among wrapped
 *  action buttons. Callers position it (e.g. `absolute right-4 top-4`) via className. */
export function CloseButton({ onClose, className = '' }: { onClose: () => void; className?: string }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close"
      className={`shrink-0 rounded-full p-1 text-[color:rgb(var(--muted))] transition hover:bg-[color:rgb(var(--tint-1))] hover:text-[color:rgb(var(--fg))] ${className}`}
    >
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
        <path d="M6 6l12 12M18 6L6 18" />
      </svg>
    </button>
  )
}

export function Card({ className = '', style, children }: { className?: string; style?: CSSProperties; children: ReactNode }) {
  return <div style={style} className={`hairline rounded-glass border bg-[color:rgb(var(--bg-elev))] ${className}`}>{children}</div>
}

/** A slim completion bar on brand tokens — the fill eases with the house flow timing. */
export function ProgressBar({ value, className = '', label }: { value: number; className?: string; label?: string }) {
  const pct = Math.max(0, Math.min(100, Math.round(value)))
  return (
    <div
      className={`h-1.5 w-full overflow-hidden rounded-full bg-tint1 ${className}`}
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div
        className="h-full rounded-full bg-[color:rgb(var(--brand-primary))] transition-[width] duration-500 [transition-timing-function:var(--ease-flow)]"
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

/**
 * A collapsible disclosure that matches the house transition language: the body grid-rows
 * its way open (no fixed max-height jump). The header takes an optional right-aligned `aside`
 * (e.g. a progress bar) and a `defaultOpen`. One control for every "hide the long form" panel.
 */
export function Accordion({
  title,
  subtitle,
  aside,
  defaultOpen = false,
  children,
}: {
  title: ReactNode
  subtitle?: ReactNode
  aside?: ReactNode
  defaultOpen?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-4 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 font-display text-xl">{title}</span>
          {subtitle && <span className="mt-0.5 block text-sm text-[color:rgb(var(--muted))]">{subtitle}</span>}
        </span>
        {aside && <span className="hidden w-40 shrink-0 sm:block">{aside}</span>}
        <span
          aria-hidden
          className={`shrink-0 text-[color:rgb(var(--muted))] transition-transform duration-300 [transition-timing-function:var(--ease-flow)] ${open ? 'rotate-180' : ''}`}
        >
          <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 6l4 4 4-4" />
          </svg>
        </span>
      </button>
      <div className={`grid transition-[grid-template-rows] duration-300 [transition-timing-function:var(--ease-flow)] ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden">{children}</div>
      </div>
    </div>
  )
}

/** `hideLabel`: the label names the control for assistive tech only (the question is already on screen beside it). */
export function Field({ label, hint, className = '', hideLabel = false, children }: { label: string; hint?: string; className?: string; hideLabel?: boolean; children: ReactNode }) {
  return (
    <label className={`block ${className}`}>
      <span className={hideLabel ? 'sr-only' : 'label-mono text-[color:rgb(var(--muted))]'}>{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="mt-1 text-xs text-[color:rgb(var(--muted))]">{hint}</p>}
    </label>
  )
}

export const inputCls =
  'w-full rounded-md border hairline bg-[color:rgb(var(--bg))] px-3 py-2 outline-none transition focus:border-[color:rgb(var(--brand-accent))] focus:ring-4 focus:ring-[color:rgb(var(--brand-accent)/0.12)] disabled:opacity-50 disabled:cursor-not-allowed'
export function Input(p: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...p} className={`${inputCls} ${p.className ?? ''}`} />
}
export function Textarea(p: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...p} className={`${inputCls} ${p.className ?? ''}`} />
}
export function Select(p: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...p} className={`${inputCls} ${p.className ?? ''}`} />
}

export function Spinner({ className = '' }: { className?: string }) {
  return (
    <div
      role="status"
      aria-label="Loading"
      className={`h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent opacity-60 ${className}`}
    />
  )
}

/** Centered page/section loader that reveals late — fast loads resolve inside the delay and never flash. */
export function LoadingPane({ className = 'py-16' }: { className?: string }) {
  return (
    <div className={`fade-in-late flex justify-center ${className}`}>
      <Spinner />
    </div>
  )
}

/** Keyboard users jump straight past the chrome (pair with id="main" on the work surface). */
export function SkipLink() {
  return (
    <a href="#main" className="skip-link btn-brass">
      Skip to content
    </a>
  )
}

/** Styled native checkbox — brand accent, comfortable hit area, system focus ring. */
export function Checkbox(p: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      type="checkbox"
      {...p}
      className={`h-4 w-4 shrink-0 cursor-pointer accent-[rgb(var(--brand-primary))] disabled:cursor-not-allowed disabled:opacity-50 ${p.className ?? ''}`}
    />
  )
}

/** Flat pill toggle (role="switch") — the one boolean control for settings rows. */
export function Switch({
  checked,
  onChange,
  disabled,
  ...aria
}: {
  checked: boolean
  onChange: () => void
  disabled?: boolean
  'aria-label'?: string
  'aria-labelledby'?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      {...aria}
      className={`relative h-6 w-11 shrink-0 rounded-full transition disabled:opacity-50 ${
        checked ? 'bg-[color:rgb(var(--brand-primary))]' : 'bg-[color:rgb(var(--fg)/0.15)]'
      }`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-[color:rgb(var(--bg-elev))] transition-all ${
          checked ? 'left-[22px]' : 'left-0.5'
        }`}
      />
    </button>
  )
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), video, [tabindex]:not([tabindex="-1"])'

/**
 * THE dialog primitive: animated veil + settling panel, Escape / veil-click to close,
 * focus moves in on open and back to the trigger on close, Tab cycles inside
 * (WCAG 2.1.2 no-keyboard-trap, the right way around), body scroll locked.
 * Children render inside a Card-style panel — pass sizing/padding via className.
 */
export function Modal({
  onClose,
  labelledBy,
  align = 'center',
  className = '',
  children,
}: {
  onClose: () => void
  /** id of the heading element inside, announced as the dialog name. */
  labelledBy?: string
  /** 'top' anchors the panel high (command palette idiom); 'center' is the default dialog. */
  align?: 'center' | 'top'
  className?: string
  children: ReactNode
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  // Latest-close ref so the listener effect runs once — re-running it on every
  // parent render would yank focus back to the panel mid-typing.
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  // Count this dialog as open so floating chrome (the update notice) steps aside while it is up.
  useEffect(() => trackModalOpen(), [])
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    const panel = panelRef.current
    // React's autoFocus runs during commit (before this effect) — don't steal it back.
    if (panel && !panel.contains(document.activeElement)) panel.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current()
      if (e.key === 'Tab' && panel) {
        const items = panel.querySelectorAll<HTMLElement>(FOCUSABLE)
        if (items.length === 0) return
        const first = items[0]!
        const last = items[items.length - 1]!
        if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
      opener?.focus()
    }
  }, [])

  // Portal to <body> so the fixed-position veil always covers the FULL viewport. Rendered in
  // place, an ancestor with backdrop-blur/transform/filter (e.g. the sticky app header) becomes
  // the containing block for `position: fixed`, clipping the veil to that box — so click-outside
  // only worked near the top. Portaling escapes any such ancestor.
  return createPortal(
    <div
      className={`modal-veil ${align === 'top' ? '!place-items-start pt-[12vh]' : ''}`}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className={`modal-pop hairline rounded-glass border bg-[color:rgb(var(--bg-elev))] outline-none max-h-[calc(100dvh-2rem)] overflow-y-auto ${align === 'top' ? 'justify-self-center' : ''} ${className}`}
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="hairline rounded-glass border border-dashed p-10 text-center">
      <p className="font-display text-xl">{title}</p>
      {hint && <p className="mx-auto mt-2 max-w-sm text-[color:rgb(var(--muted))]">{hint}</p>}
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  )
}

/**
 * Pane-level fetch failure. A failed load must NEVER render as an empty state ("no data yet")
 * — that misreports outages/authz errors as missing data (a prod bug report read exactly that
 * way). Pair with useData: `x.error ? <FetchError error={x.error} retry={x.reload} /> : …`.
 */
export function FetchError({ error, retry, className = '' }: { error: string; retry?: () => void; className?: string }) {
  return (
    <div role="alert" className={`rounded-card bg-[color:rgb(var(--danger)/0.08)] px-4 py-3 text-sm text-clay ${className}`}>
      Couldn't load: {error}.
      {retry && (
        <button type="button" onClick={retry} className="ml-2 font-medium underline">
          Try again
        </button>
      )}
    </div>
  )
}

export interface ConfirmRequest {
  title: string
  body?: string
  /** Defaults to "Delete" — the destructive idiom this dialog exists for. */
  confirmLabel?: string
  action: () => void
}

/**
 * In-brand replacement for window.confirm(): a small Modal with the full dialog
 * a11y contract (focus, Escape, Tab containment) instead of the native chrome box.
 */
export function ConfirmDialog({ request, onClose }: { request: ConfirmRequest; onClose: () => void }) {
  return (
    <Modal onClose={onClose} labelledBy="confirm-title" className="w-full max-w-sm p-6">
      <h3 id="confirm-title" className="font-display text-xl">{request.title}</h3>
      {request.body && <p className="mt-2 text-sm text-[color:rgb(var(--muted))]">{request.body}</p>}
      <div className="mt-5 flex justify-end gap-2">
        {/* Focus lands on the safe action (APG dialog pattern) — Enter never deletes by accident. */}
        <Button variant="ghost" autoFocus onClick={onClose}>Cancel</Button>
        <Button
          className="!bg-[color:rgb(var(--danger))]"
          onClick={() => {
            request.action()
            onClose()
          }}
        >
          {request.confirmLabel ?? 'Delete'}
        </Button>
      </div>
    </Modal>
  )
}

/** One-liner confirm flow: `confirm({ title, action })` opens the dialog; render `element` once. */
export function useConfirm() {
  const [request, setRequest] = useState<ConfirmRequest | null>(null)
  const element = request ? <ConfirmDialog request={request} onClose={() => setRequest(null)} /> : null
  return { confirm: setRequest, element }
}

export function StageBadge({ stage }: { stage: OpportunityStage }) {
  return (
    <span
      className="inline-block rounded-full px-3 py-1 text-xs"
      style={{ background: 'rgb(var(--tint-3))', color: 'rgb(var(--brand-primary))' }}
    >
      {STAGE_META[stage]?.label ?? stage}
    </span>
  )
}
